import { Box, FormControl, MenuItem, Select } from "@mui/material"
import { useSearch } from "@tanstack/react-router"
import React, { useCallback, useEffect, useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { BANKING_FIELDS_DEFAULT_VALUES, BANKING_FIELDS_KEYS } from "../constants"
import type { PaymentMethodFormValues } from "../schemas/PaymentMethodSchema"

import BankingMethodFields from "./components/BankingMethodFields"

import LocationSelector from "@/components/Forms/LocationSelector"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import type { CompanyPaymentType } from "@/graphql/codegen/graphql"
import { PaymentAccountType, PaymentMethod } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { useGetAssociatedLocations } from "@/screens/Companies/hooks"
import { usePaymentTermsContext } from "@/screens/CompanySettings/Accounting/contexts/PaymentTermsContext"

const paymentMethodOptions = [
  { value: PaymentMethod.Wire, label: "Wire (EFT)" },
  { value: PaymentMethod.Ach, label: "ACH" },
  { value: PaymentMethod.CreditCard, label: "Credit Card" },
  { value: PaymentMethod.Check, label: "Check" },
  { value: PaymentMethod.Cash, label: "Cash" },
]

interface PaymentMethodFormProps {
  companyId: string
  paymentTypeToCreateFrom?: CompanyPaymentType | null
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ companyId, paymentTypeToCreateFrom }) => {
  const searchParams = useSearch({ from: "/app/companies/$companyId/edit/_layout/accounting" })
  const methods = useFormContext<PaymentMethodFormValues>()
  const { watch, setValue } = methods
  const { locations } = useGetLocations(companyId)
  const { associatedLocationList: allAssociatedLocationList } = useGetAssociatedLocations({ companyId })
  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()

  const watchedMethod = watch("method")
  const watchedLocationId = watch("locationId")
  const isBankingMethod = watchedMethod === PaymentMethod.Wire || watchedMethod === PaymentMethod.Ach

  // Build account type options based on company type
  const availableAccountTypeOptions = useMemo(
    () => [
      ...(isCompanyVendor ? [{ value: PaymentAccountType.Payable, label: "Accounts Payable" }] : []),
      ...(isCompanyCustomer ? [{ value: PaymentAccountType.Receivable, label: "Accounts Receivable" }] : []),
    ],
    [isCompanyVendor, isCompanyCustomer]
  )

  const associatedLocationList = useMemo(() => {
    const allBillingLocations = allAssociatedLocationList.filter(
      (associatedLocation) => associatedLocation.billing?.locationId
    )
    return locations.filter((location) =>
      allBillingLocations.some((associatedLocation) => associatedLocation.billing?.locationId === location.locationId)
    )
  }, [locations, allAssociatedLocationList])

  // Auto-select location when only one is available
  useEffect(() => {
    if (associatedLocationList.length && !watchedLocationId) {
      setValue("locationId", associatedLocationList[0].locationId)
    }
  }, [associatedLocationList, setValue, watchedLocationId])

  // Reset banking fields when switching from banking to non-banking method
  useEffect(() => {
    if (!isBankingMethod) {
      BANKING_FIELDS_KEYS.forEach((field) => {
        setValue(field, BANKING_FIELDS_DEFAULT_VALUES[field])
      })
    }
  }, [isBankingMethod, setValue])

  // Auto-select account type when only one is available
  useEffect(() => {
    if (availableAccountTypeOptions.length === 1) {
      setValue("accountType", availableAccountTypeOptions[0].value)
    }
  }, [availableAccountTypeOptions, setValue])

  const filterPaymentMethodOptions = useCallback(
    (option: { value: PaymentMethod }) => {
      if (!searchParams?.companyPaymentTypeId) {
        return true
      }

      return option.value === PaymentMethod.Wire || option.value === PaymentMethod.Ach
    },
    [searchParams?.companyPaymentTypeId]
  )

  return (
    <Box className='space-y-6'>
      <LocationSelector
        name='locationId'
        label='Add Company Billing Location'
        companyId={companyId}
        locations={associatedLocationList}
        placeholder='Select Location'
      />

      <Controller
        name='accountType'
        control={methods.control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth>
            <label className={FIELD_LABEL_CLASSES}>
              Account Type (Receivable or Payable)<span className='text-red-600'>*</span>
            </label>
            <Select
              {...field}
              {...SELECT_STYLING_PROPS}
              error={!!error}
              renderValue={(selected) => {
                if (!selected) {
                  return <span className='text-gray-500'>Select Account Type</span>
                }
                const selectedType = availableAccountTypeOptions.find(({ value }) => value === selected)
                return selectedType?.label || String(selected)
              }}
            >
              {availableAccountTypeOptions.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {error && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </FormControl>
        )}
      />

      <Controller
        name='method'
        control={methods.control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth>
            <label className={FIELD_LABEL_CLASSES}>
              Payment Method<span className='text-red-600'>*</span>
            </label>
            <Select
              {...field}
              {...SELECT_STYLING_PROPS}
              error={!!error}
              renderValue={(selected) => {
                if (!selected) {
                  return <span className='text-gray-500'>Select Payment Method</span>
                }

                const selectedMethod = paymentMethodOptions.find(({ value }) => value === selected)
                return selectedMethod?.label || String(selected)
              }}
            >
              {paymentMethodOptions.filter(filterPaymentMethodOptions).map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
            {error && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </FormControl>
        )}
      />

      {isBankingMethod && <BankingMethodFields paymentTypeToCreateFrom={paymentTypeToCreateFrom} />}
    </Box>
  )
}

export default PaymentMethodForm
